import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import LinearProgress from '@mui/material/LinearProgress'
import Link from '@mui/material/Link'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { problemMessage, statusOf } from '../../api/baseApi'
import {
  operator as operatorSurface,
  useBindTenantTrafficMutation,
  useCollectTenantTrafficMutation,
  useListTenantTrafficQuery,
  useListTrafficConnectionsQuery,
  useListTrafficSourcesQuery,
  useUnbindTenantTrafficMutation,
  type TrafficSite,
  type TrafficStatus,
} from '../../api/operator'
import { shown } from '../../api/useIntent'
import { siteTrafficTag } from '../../api/baseApi'
import type { AppDispatch } from '../../store'
import { SchemaForm } from '../../editors/SchemaForm'
import { operator } from '../../model/routes'
import type { Schema } from '../../model/schema'
import { dayText, healthText, instantDayText } from '../../model/traffic'
import { Loading, QueryError } from '../../ui/QueryState'

/**
 * Which of a tenant's sites have their visits collected, from which
 * connection, and how that is going (docs/plan/site-traffic.md §9.1).
 *
 * **One row per site, named by its main address**, never by its id: a
 * Website's uuid on screen is the mistake the Domains tab made and this
 * one does not repeat. A site whose addresses have all been released is
 * named by the address its binding recorded, because the operator
 * surface holds no read model to name it any other way.
 *
 * **Every host is listed, counted or not**, and so are the tenant's
 * granted hosts no site answers on: a missing `www.` is visible here
 * rather than quietly halving a site's numbers. Under a binding each
 * counted host says the day it is counted from, because one stated
 * history date does not cover them all: an address granted after the
 * date was stated counts from its own grant.
 *
 * **A collection is followed until it has reached the site**, by
 * re-reading the list as the purge's progress is followed, because the
 * run is a queue message away and its result is the next read, not the
 * answer to the press.
 */
export function TenantTraffic({ tenant, active }: { tenant: string; active: boolean }) {
  const traffic = useListTenantTrafficQuery({ tenant })
  const connections = useListTrafficConnectionsQuery()
  const [collect, collecting] = useCollectTenantTrafficMutation()
  const [connecting, setConnecting] = useState<TrafficSite | null>(null)
  const [disconnecting, setDisconnecting] = useState<TrafficSite | null>(null)
  const [watching, setWatching] = useState<Record<string, Watch>>({})
  const [late, setLate] = useState<Record<string, boolean>>({})

  /**
   * Follow `website` until a run has reached it since `before`, the
   * `lastRunAt` the row showed when the button was pressed.
   *
   * **The row as drawn, not the press's answer.** The platform reads the
   * site for its answer after queueing the run, so a run that finishes
   * first — the local pump's always can — is already in the answer, and
   * following from there would wait for a second run that is not coming.
   */
  const watch = (website: string, before: string | undefined) => {
    setLate((was) => ({ ...was, [website]: false }))
    setWatching((was) => ({ ...was, [website]: { before, until: Date.now() + FOLLOW_FOR } }))
  }

  // Re-read while anything is being followed, and give up on a run that
  // has not reported back in time. Every run that reaches a site stamps
  // it, one with no day to collect included, so this is a queue that is
  // slow or a run that failed before it reached the site.
  const { refetch } = traffic
  useEffect(() => {
    const websites = Object.keys(watching)
    if (websites.length === 0) return
    const timer = window.setInterval(() => {
      void refetch()
      const now = Date.now()
      const expired = websites.filter((website) => watching[website].until < now)
      if (expired.length === 0) return
      setLate((was) => ({ ...was, ...Object.fromEntries(expired.map((website) => [website, true])) }))
      setWatching((was) => Object.fromEntries(Object.entries(was).filter(([website]) => !expired.includes(website))))
    }, FOLLOW_EVERY)
    return () => window.clearInterval(timer)
  }, [watching, refetch])

  // A run has reached the site once its `lastRunAt` is not the one the
  // row showed when the button was pressed. Its days are new then, so the
  // customer's card for the site is re-read too, where it is held.
  const sites = traffic.data?.sites
  const dispatch = useDispatch<AppDispatch>()
  useEffect(() => {
    if (!sites) return
    const reached = Object.keys(watching).filter((website) => {
      const lastRunAt = sites.find((site) => site.website === website)?.binding?.lastRunAt
      return lastRunAt !== undefined && lastRunAt !== watching[website].before
    })
    if (reached.length === 0) return
    dispatch(operatorSurface.util.invalidateTags(reached.map((website) => siteTrafficTag(tenant, website))))
    setWatching((was) => Object.fromEntries(Object.entries(was).filter(([website]) => !reached.includes(website))))
  }, [sites, watching, dispatch, tenant])

  if (traffic.isLoading) return <Loading label="Loading traffic…" />
  if (traffic.error || !traffic.data) return <QueryError error={traffic.error} title="Could not load traffic" />
  const { unassignedHosts } = traffic.data
  const listed = traffic.data.sites
  const connectionLabel = (ref: string) => connections.data?.connections.find((entry) => entry.ref === ref)?.label ?? ref

  return (
    <Stack spacing={2}>
      {Boolean(collecting.error) && (
        <Alert severity="error">{problemMessage(collecting.error, 'The collection was not started.')}</Alert>
      )}
      <Paper>
        {listed.length === 0 ? (
          <Stack spacing={1} sx={{ p: 2 }} alignItems="flex-start">
            <Typography color="text.secondary">
              No site has a web address yet, so there is nothing to collect visits for.
            </Typography>
            <Link component={RouterLink} to={operator.tenantDomains(tenant)} variant="body2">
              Grant a domain on the Domains tab
            </Link>
          </Stack>
        ) : (
          <List disablePadding>
            {listed.map((site) => (
              <SiteRow
                key={site.website}
                site={site}
                active={active}
                connectionLabel={connectionLabel}
                following={Boolean(watching[site.website])}
                late={Boolean(late[site.website])}
                collecting={collecting.isLoading && collecting.originalArgs?.website === site.website}
                onConnect={() => setConnecting(site)}
                onDisconnect={() => setDisconnecting(site)}
                onCollect={() => {
                  const before = site.binding?.lastRunAt
                  collect({ tenant, website: site.website })
                    .unwrap()
                    .then(() => watch(site.website, before))
                    .catch(shown)
                }}
              />
            ))}
          </List>
        )}
      </Paper>
      {unassignedHosts.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h3">Not counted</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Granted to this tenant, but no site answers on them, so their visits are not collected.
          </Typography>
          <List dense disablePadding>
            {unassignedHosts.map((host) => (
              <ListItem key={host} disableGutters>
                <ListItemText primary={host} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
      {connecting && (
        <ConnectDialog
          tenant={tenant}
          site={connecting}
          onClose={() => setConnecting(null)}
          onConnected={() => {
            setConnecting(null)
            watch(connecting.website, connecting.binding?.lastRunAt)
          }}
          onReload={() => {
            setConnecting(null)
            void refetch()
          }}
        />
      )}
      {disconnecting && (
        <DisconnectDialog
          tenant={tenant}
          site={disconnecting}
          onClose={() => setDisconnecting(null)}
          onReload={() => {
            setDisconnecting(null)
            void refetch()
          }}
        />
      )}
    </Stack>
  )
}

interface Watch {
  /** `lastRunAt` as the press was answered with it. */
  before?: string
  /** When to stop re-reading, in milliseconds since the epoch. */
  until: number
}

/** How often a collection is re-read for, and for how long. */
const FOLLOW_EVERY = 1500
const FOLLOW_FOR = 60_000

/**
 * What each status is called and coloured, in a chip short enough to
 * scan down a column. The detail goes in the line under it.
 */
const statuses: Record<TrafficStatus, { label: string; colour: 'default' | 'success' | 'warning' | 'error' }> = {
  unbound: { label: 'Not connected', colour: 'default' },
  pending: { label: 'Pending', colour: 'default' },
  ok: { label: 'Collecting', colour: 'success' },
  failing: { label: 'Failing', colour: 'warning' },
  refused: { label: 'Needs attention', colour: 'error' },
  window: { label: 'Window', colour: 'warning' },
  noAddress: { label: 'No address', colour: 'warning' },
  noConnection: { label: 'Connection missing', colour: 'error' },
  notConfigured: { label: 'No credentials', colour: 'warning' },
}

function TrafficChip({ status }: { status: TrafficStatus }) {
  const { label, colour } = statuses[status]
  return <Chip size="small" variant="outlined" color={colour} label={label} />
}

const when = (instant: string) => new Date(instant).toLocaleString()

/** The span of days held for a site: "Collected 5 January 2026 to 13 October 2026". */
function collectedText(binding: { collectedFrom?: string; collectedThrough?: string }): string {
  const through = dayText(binding.collectedThrough ?? '')
  return binding.collectedFrom ? `Collected ${dayText(binding.collectedFrom)} to ${through}` : `Collected through ${through}`
}

function SiteRow({
  site,
  active,
  connectionLabel,
  following,
  late,
  collecting,
  onConnect,
  onDisconnect,
  onCollect,
}: {
  site: TrafficSite
  active: boolean
  connectionLabel: (ref: string) => string
  following: boolean
  late: boolean
  collecting: boolean
  onConnect: () => void
  onDisconnect: () => void
  onCollect: () => void
}) {
  const binding = site.binding
  const bound = Boolean(binding?.connection)
  const counted = site.hosts.filter((host) => host.counted)
  const uncounted = site.hosts.filter((host) => !host.counted)
  const title = site.hosts.length === 0 ? `${site.label} (no web address now)` : site.label

  return (
    <ListItem divider sx={{ display: 'block', py: 1.5 }} aria-label={title}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'flex-start' }}>
        <ListItemText
          primary={
            <Stack direction="row" spacing={1} alignItems="center" component="span">
              <Box component="span" sx={{ fontWeight: 600 }}>
                {title}
              </Box>
              <TrafficChip status={binding?.status ?? 'unbound'} />
            </Stack>
          }
          secondaryTypographyProps={{ component: 'div' }}
          secondary={
            <Stack spacing={0.25} sx={{ mt: 0.5 }}>
              {binding && <StatusDetail status={binding.status} binding={binding} connectionLabel={connectionLabel} />}
              {bound && binding?.connection && (
                <span>
                  Through {connectionLabel(binding.connection)}
                  {binding.historyFrom && (
                    <>
                      {' '}
                      · History from {dayText(binding.historyFrom)}
                      {binding.vouchedAt && <>, stated {instantDayText(binding.vouchedAt)}</>}
                    </>
                  )}
                </span>
              )}
              {!bound && binding?.collectedThrough && <span>{collectedText(binding)}, and kept.</span>}
              {/* One line a host, because the stated date does not reach
                  them all: one granted after it counts from its own grant. */}
              {counted.map((host) => (
                <span key={host.host}>
                  {host.host}
                  {host.canonical && ' (main)'} ·{' '}
                  {host.countedFrom ? `Counted from ${dayText(host.countedFrom)}` : 'Counted'}
                </span>
              ))}
              {uncounted.length > 0 && <span>Not counted: {uncounted.map((host) => host.host).join(', ')}</span>}
              {following && (
                <Box component="span" sx={{ display: 'block', pt: 0.5 }}>
                  <span>Collecting now…</span>
                  <LinearProgress sx={{ mt: 0.5, maxWidth: 240 }} />
                </Box>
              )}
              {late && <span>The collection has not reached this site yet. Read this page again later.</span>}
            </Stack>
          }
        />
        {active && (
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            {counted.length > 0 && (
              <Button variant={bound ? 'text' : 'outlined'} onClick={onConnect}>
                {bound ? 'Change connection' : 'Connect'}
              </Button>
            )}
            {bound && (
              <>
                <Button disabled={collecting || following} onClick={onCollect}>
                  Collect now
                </Button>
                <Button color="error" onClick={onDisconnect}>
                  Disconnect
                </Button>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </ListItem>
  )
}

/** The status's own line, where it has one. */
function StatusDetail({
  status,
  binding,
  connectionLabel,
}: {
  status: TrafficStatus
  binding: NonNullable<TrafficSite['binding']>
  connectionLabel: (ref: string) => string
}) {
  switch (status) {
    case 'ok':
      return binding.collectedThrough ? <span>{collectedText(binding)}</span> : null
    case 'failing':
      return (
        <>
          <span>
            Failing since {binding.failingSince ? when(binding.failingSince) : 'the last run'}
            {binding.lastRunAt && <>, last tried {when(binding.lastRunAt)}</>}
          </span>
          {binding.lastError && <span>{binding.lastError}</span>}
        </>
      )
    case 'refused':
    case 'window':
      return <span>{binding.lastError}</span>
    case 'noConnection':
      return <span>{binding.connection} is not a connection this deployment declares.</span>
    case 'notConfigured':
      return (
        <span>
          {connectionLabel(binding.connection ?? '')} has no credentials. Add them in{' '}
          <Link component={RouterLink} to={operator.connections()}>
            Traffic connections
          </Link>
          .
        </span>
      )
    default:
      return null
  }
}

/**
 * Connect a site, or change the connection it is collected through.
 *
 * **A connection without credentials can still be chosen**, and says so:
 * the run a bind starts reads the token then, so binding first and
 * pasting the token after is a working order (plan §7.2). The dialog
 * points at where the token goes rather than refusing.
 *
 * The source's own binding settings are drawn from the schema it
 * publishes, where it publishes one; neither source in v1 does, so the
 * dialog is usually the picker and the date.
 *
 * **It binds over the binding it was opened on, and no other.** The
 * `revision` the row showed goes back as `expectedRevision` on every
 * bind over a binding, connected or not, and a site never bound sends
 * none. Every bind and every disconnect advances it, so a binding that
 * moved while the dialog was open — changed or disconnected, by another
 * operator or another tab — is a 409, which is said and offered a
 * reload, never retried: what the operator chose was chosen against a
 * site that is no longer there. `boundAt` is not the version: a
 * disconnect leaves it where it was.
 *
 * **Saving the date a binding already holds states nothing new**, so it
 * does not reach an address granted since it was first stated (plan
 * §5.2). Where that is what the operator means, they say so with the
 * box, which is offered only while the date is the binding's own.
 */
function ConnectDialog({
  tenant,
  site,
  onClose,
  onConnected,
  onReload,
}: {
  tenant: string
  site: TrafficSite
  onClose: () => void
  onConnected: () => void
  onReload: () => void
}) {
  const connections = useListTrafficConnectionsQuery()
  const sources = useListTrafficSourcesQuery()
  const [bind, binding] = useBindTenantTrafficMutation()
  const changing = Boolean(site.binding?.connection)
  const declared = connections.data?.connections ?? []
  const [chosen, setChosen] = useState(site.binding?.connection ?? '')
  const held = site.binding?.historyFrom
  const [historyFrom, setHistoryFrom] = useState(held ?? '')
  const [restate, setRestate] = useState(false)
  const repeating = Boolean(held) && historyFrom === held
  // The binding as the dialog was opened on it, held so a list re-read
  // behind the dialog cannot move it.
  const [expectedRevision] = useState(site.binding?.revision)
  const [settings, setSettings] = useState<Record<string, unknown>>({})
  const conflict = statusOf(binding.error) === 409
  const connection = declared.find((entry) => entry.ref === chosen)
  const source = sources.data?.sources.find((entry) => entry.kind === connection?.kind)
  const schema = source?.binding as Schema | undefined
  const asks = Object.keys(schema?.properties ?? {}).length > 0
  const sourceLabel = (kind: string) => sources.data?.sources.find((entry) => entry.kind === kind)?.label ?? kind

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{changing ? `Change the connection for ${site.label}` : `Connect ${site.label}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {connections.isLoading ? (
            <Loading label="Loading connections…" />
          ) : connections.error ? (
            <QueryError error={connections.error} title="Could not load connections" />
          ) : declared.length === 0 ? (
            <DialogContentText>No traffic connections are declared for this deployment.</DialogContentText>
          ) : (
            <FormControl>
              <FormLabel id="traffic-connection">Connection</FormLabel>
              <RadioGroup
                aria-labelledby="traffic-connection"
                value={chosen}
                onChange={(event) => {
                  setChosen(event.target.value)
                  setSettings({})
                }}
              >
                {declared.map((entry) => (
                  <FormControlLabel
                    key={entry.ref}
                    value={entry.ref}
                    control={<Radio />}
                    label={
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2">{entry.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {sourceLabel(entry.kind)} · {healthText(entry)}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
          {connection && connection.configured !== true && (
            <Alert severity="info">
              {connection.label} {connection.configured === false ? 'has no credentials' : 'has not been checked yet'}. It
              can still be chosen: collection starts once its token is in place, which{' '}
              <Link component={RouterLink} to={operator.connections()}>
                Traffic connections
              </Link>{' '}
              explains.
            </Alert>
          )}
          <TextField
            label="History from"
            type="date"
            value={historyFrom}
            onChange={(event) => setHistoryFrom(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            helperText={
              'Optional. The day this tenant has held the site’s web addresses from. Without it, visits are counted from the first whole day after each address was granted. The date covers the addresses the site holds when you state it; an address granted later counts from its own grant.' +
              (held
                ? ` Saving ${dayText(held)} again does not cover an address granted since it was first stated${site.binding?.vouchedAt ? ` on ${instantDayText(site.binding.vouchedAt)}` : ''}, unless the box below is ticked.`
                : '')
            }
          />
          {repeating && (
            <FormControlLabel
              control={<Checkbox checked={restate} onChange={(event) => setRestate(event.target.checked)} />}
              label="State this date again for the addresses the site holds now"
            />
          )}
          {asks && schema && (
            <SchemaForm schema={schema} root={schema} value={settings} onChange={setSettings} disabled={binding.isLoading} />
          )}
          {conflict ? (
            <ChangedBehind onReload={onReload} />
          ) : (
            Boolean(binding.error) && (
              <Alert severity="error">
                {problemMessage(binding.error, changing ? 'The connection was not changed.' : 'The site was not connected.')}
              </Alert>
            )
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!connection || binding.isLoading || conflict}
          onClick={() =>
            bind({
              tenant,
              website: site.website,
              bindTraffic: {
                connection: chosen,
                ...(historyFrom ? { historyFrom } : {}),
                ...(repeating && restate ? { restateHistory: true } : {}),
                ...(expectedRevision ? { expectedRevision } : {}),
                ...(asks ? { settings } : {}),
              },
            })
              .unwrap()
              .then(onConnected)
              .catch(shown)
          }
        >
          {changing ? 'Change connection' : 'Connect'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * A binding that moved while its dialog was open: said, with a reload,
 * and never retried on the operator's behalf.
 */
function ChangedBehind({ onReload }: { onReload: () => void }) {
  return (
    <Alert
      severity="error"
      action={
        <Button color="inherit" size="small" onClick={onReload}>
          Reload
        </Button>
      }
    >
      This site’s connection changed while the dialog was open. Reload and try again.
    </Alert>
  )
}

/**
 * One confirmation in front of a disconnect, saying what is kept.
 *
 * **It disconnects the binding it was opened on, and no other**, by the
 * same `revision` the connect dialog pins: a site changed or reconnected
 * behind it is a 409, said and offered a reload, so a confirmation given
 * for one connection never takes down another.
 */
function DisconnectDialog({
  tenant,
  site,
  onClose,
  onReload,
}: {
  tenant: string
  site: TrafficSite
  onClose: () => void
  onReload: () => void
}) {
  const [unbind, unbinding] = useUnbindTenantTrafficMutation()
  const [expectedRevision] = useState(site.binding?.revision)
  const conflict = statusOf(unbinding.error) === 409
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Disconnect {site.label}?</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>
            Numbers collected so far stay. Nothing is collected while disconnected; reconnecting fills in as far back as the
            source keeps.
          </DialogContentText>
          {conflict ? (
            <ChangedBehind onReload={onReload} />
          ) : (
            Boolean(unbinding.error) && (
              <Alert severity="error">{problemMessage(unbinding.error, 'The site was not disconnected.')}</Alert>
            )
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="error"
          variant="contained"
          disabled={unbinding.isLoading || conflict}
          onClick={() =>
            unbind({ tenant, website: site.website, expectedRevision })
              .unwrap()
              .then(onClose)
              .catch(shown)
          }
        >
          Disconnect
        </Button>
      </DialogActions>
    </Dialog>
  )
}
